import { cn } from '@/shared/utilities';
import { Close, Pause, PlayArrow } from '@mui/icons-material';
import { IconButton, Stack, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { AudioAttachment } from '../../models/attachment';
import { useAttachmentUrl } from '../../queries/use-attachment-url';

interface ComposeAudioProps {
  attachment: AudioAttachment;
  onRemove?: (attachmentId: string) => void;
  mode?: 'compact' | 'comfortable';
  isOwner: boolean;
}

export const ComposeAudio: React.FC<ComposeAudioProps> = React.memo(
  ({ attachment, onRemove, mode = 'comfortable' }) => {
    const isCompact = mode === 'compact';

    // Get the audio URL - use blobUrl for preview, then url for final
    const audioUrl = attachment.blobUrl || attachment.url;
    const { blobUrl, isError } = useAttachmentUrl(audioUrl, !attachment.blobUrl);

    // Use the blobUrl if available, otherwise use the original URL
    const finalAudioUrl = blobUrl || audioUrl;

    // State for play/pause control
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    // Refs
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const audioRef = useRef<HTMLAudioElement>(null);
    const isDraggingRef = useRef(false);
    const animationFrameRef = useRef<number | null>(null);

    // Get waveform data (100 bars) - data is in 0-120 dB scale
    // Fallback to 60 dB (middle value) if no waveform data
    const waveformData = attachment.waveforms || new Array(100).fill(60);

    // Initialize audio element
    useEffect(() => {
      if (!finalAudioUrl) return;

      const audio = new Audio(finalAudioUrl);
      audioRef.current = audio;

      const handleLoadedMetadata = () => {
        setDuration(audio.duration);
      };

      const handleTimeUpdate = () => {
        if (!isDraggingRef.current) {
          setCurrentTime(audio.currentTime);
        }
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
        audio.currentTime = 0;
      };

      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', handleEnded);

      return () => {
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('ended', handleEnded);
        audio.pause();
        audio.src = '';
      };
    }, [finalAudioUrl]);

    // Toggle play/pause
    const togglePlayPause = useCallback(() => {
      if (!audioRef.current) return;

      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }, [isPlaying]);

    if (!finalAudioUrl) {
      return null;
    }

    // Draw waveform with progress
    const drawWaveform = useCallback(() => {
      if (!canvasRef.current) return;

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Calculate dynamic bar count based on canvas width
      // Each bar: 2px width + 1px gap = 3px total (matching audio-recorder)
      const barWidth = 2;
      const barGap = 1;
      const barCount = Math.floor(canvas.width / (barWidth + barGap));
      const baseLineY = canvas.height - 2;

      // Calculate progress position (0 to 1)
      const progress = duration > 0 ? currentTime / duration : 0;
      const progressBarIndex = Math.floor(progress * barCount);

      // Draw base line
      // ctx.fillStyle = '#1966FF';
      ctx.fillRect(0, baseLineY, canvas.width, 0.5);

      // Get theme colors from CSS custom properties
      const primaryColor = getComputedStyle(canvas).getPropertyValue('--mui-palette-primary-main') || '#1976d2';
      const primaryDarkColor = getComputedStyle(canvas).getPropertyValue('--mui-palette-primary-dark') || '#1565c0';
      const primaryLightColor = getComputedStyle(canvas).getPropertyValue('--mui-palette-primary-light') || '#42a5f5';

      // Draw bars
      for (let i = 0; i < barCount; i++) {
        const dataIndex = Math.floor((i / barCount) * waveformData.length);
        const dbValue = waveformData[dataIndex] || 60; // Value in 0-120 dB range

        // Normalize dB value (0-120) to 0-1 range
        const normalizedValue = Math.min(dbValue / 120, 1);
        const maxBarHeight = canvas.height - 4; // Leave 2px for baseline at bottom and 2px padding at top
        const minBarHeight = 2; // Minimum bar height for visibility
        const barHeight = Math.max(minBarHeight, normalizedValue * maxBarHeight);

        const x = i * (barWidth + barGap);
        const y = baseLineY - barHeight;

        // Determine if this bar is in the played region
        const isPlayed = i <= progressBarIndex;

        // Create gradient (bottom to top)
        const gradient = ctx.createLinearGradient(0, baseLineY, 0, y);

        if (isPlayed) {
          // Played bars - use primary and primary dark
          gradient.addColorStop(0, primaryColor);
          gradient.addColorStop(1, primaryDarkColor);
        } else {
          // Unplayed bars - use primary light variants
          gradient.addColorStop(0, primaryLightColor);
          const lighterColor = getComputedStyle(canvas).getPropertyValue('--mui-palette-primary-lighter') || '#90caf9';
          gradient.addColorStop(1, lighterColor);
        }

        ctx.fillStyle = gradient;
        ctx.fillRect(x, y, barWidth, barHeight);
      }

      // Draw cursor line at current position
      const cursorX = progress * canvas.width;
      ctx.fillStyle = primaryColor;
      ctx.fillRect(cursorX - 1, 0, 2, canvas.height);
    }, [waveformData, currentTime, duration]);

    // Smooth animation loop at 60 FPS
    useEffect(() => {
      if (!isPlaying) return;

      const animate = () => {
        drawWaveform();
        animationFrameRef.current = requestAnimationFrame(animate);
      };

      animate();

      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
      };
    }, [isPlaying, drawWaveform]);

    // Redraw waveform when not playing (for seek or state changes)
    useEffect(() => {
      if (!isPlaying) {
        drawWaveform();
      }
    }, [isPlaying, drawWaveform]);

    // Resize canvas to match display size
    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const resizeCanvas = () => {
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width;
        canvas.height = rect.height;
        drawWaveform();
      };

      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);

      return () => {
        window.removeEventListener('resize', resizeCanvas);
      };
    }, [drawWaveform]);

    // Handle seek on canvas click/drag
    const handleSeek = useCallback(
      (clientX: number) => {
        if (!canvasRef.current || !audioRef.current) return;

        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const x = clientX - rect.left;
        const progress = Math.max(0, Math.min(1, x / rect.width));
        const newTime = progress * duration;

        audioRef.current.currentTime = newTime;
        setCurrentTime(newTime);
      },
      [duration]
    );

    const handleMouseDown = useCallback(
      (e: React.MouseEvent<HTMLCanvasElement>) => {
        isDraggingRef.current = true;
        handleSeek(e.clientX);
      },
      [handleSeek]
    );

    const handleMouseMove = useCallback(
      (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (isDraggingRef.current) {
          handleSeek(e.clientX);
        }
      },
      [handleSeek]
    );

    const handleMouseUp = useCallback(() => {
      isDraggingRef.current = false;
    }, []);

    const handleMouseLeave = useCallback(() => {
      isDraggingRef.current = false;
    }, []);

    // Format time helper
    const formatTime = (seconds: number): string => {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
      <div
        className={cn(
          'mex:group/mex:audio mex:relative mex:overflow-hidden mex:transition-all',
          isCompact && 'mex:max-w-[320px]'
        )}
      >
        {/* Content container */}
        <div className='mex:p-3'>
          {/* Audio player container */}
          <div className='mex:flex mex:items-center mex:gap-3'>
            {/* Play/Pause button */}
            <IconButton
              size='small'
              onClick={togglePlayPause}
              disabled={!finalAudioUrl || isError}
              className='mex:flex-shrink-0 mex:bg-white mex:shadow-sm mex:transition-colors mex:hover:bg-gray-100 mex:dark:bg-gray-800 mex:dark:hover:bg-gray-700'
            >
              {isPlaying ? (
                <Pause className='mex:!size-4 mex:text-gray-700 mex:dark:text-gray-200' />
              ) : (
                <PlayArrow className='mex:!size-4 mex:text-gray-700 mex:dark:text-gray-200' />
              )}
            </IconButton>

            {/* Audio info and controls */}
            <div className='mex:min-w-[250px] mex:flex-1'>
              <Stack direction='column' spacing={1}>
                {/* Canvas Waveform */}
                <canvas
                  ref={canvasRef}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseLeave}
                  className={cn(
                    'mex:h-[40px] mex:w-full mex:cursor-pointer mex:rounded mex:bg-gray-100 mex:dark:bg-primary-900/30',
                    isError && 'mex:opacity-50'
                  )}
                  style={{ display: 'block' }}
                  aria-label='Audio waveform - click or drag to seek'
                />

                {/* Time display */}
                <Typography variant='caption' className='mex:text-gray-500 mex:text-xs mex:dark:text-gray-400'>
                  {formatTime(currentTime)} / {formatTime(duration || (attachment.durationMs || 0) / 1000)}
                </Typography>
              </Stack>
            </div>
          </div>

          {/* Error state */}
          {isError && (
            <Typography variant='caption' className='mex:mt-2 mex:text-red-500 mex:text-xs'>
              Failed to load audio file
            </Typography>
          )}
        </div>

        {/* Remove Button (if onRemove is provided) */}
        {onRemove && (
          <IconButton
            size='small'
            onClick={() => onRemove(attachment.id || '')}
            className='mex:absolute mex:top-2 mex:right-2 mex:bg-white mex:text-gray-400 mex:shadow-sm mex:transition-colors mex:hover:bg-red-50 mex:hover:text-red-500 mex:dark:bg-gray-800 mex:dark:text-gray-500 mex:dark:hover:text-red-400'
            sx={{
              '&:hover': {
                backgroundColor: 'error.lighter'
              }
            }}
          >
            <Close className='mex:!size-4' />
          </IconButton>
        )}
      </div>
    );
  }
);

ComposeAudio.displayName = 'ComposeAudio';
